import React, { useCallback, useMemo, useRef, useState } from 'react';

import { useVirtualizer } from '@tanstack/react-virtual';

import useTranslation from '../../../../features/locale/useTranslation';
import {
    SELECT_ALL_OPTION_KEY,
    SELECT_DEFAULT_OPTION_FILTER_PROP,
    SELECT_DEFAULT_OPTION_KEY_PROP
} from '../../Select.constants';
import { parseOptions, flattenOptions, getOptionIdentifier } from '../../Select.helpers';
import {
    type InternalSelectOption,
    type SelectOption,
    type SelectRootProps
} from '../../Select.types';

import { useFilterOptions } from './useFilterOptions';

type SelectOptionsProps = {
    options: SelectOption[];
    disableAutoActiveFirstOption: boolean;
    internalSearchValue: string;
    autoComplete: boolean;
    isVirtualScrollEnabled: boolean;
    virtualScrollContainer: React.RefObject<HTMLDivElement | null>;
} & Pick<
    SelectRootProps,
    | 'rowComponent'
    | 'mode'
    | 'withSelectAll'
    | 'filterOption'
    | 'optionFilterProp'
    | 'optionKeyProp'
    | 'value'
>;

export const useSelectOptions = ({
    options,
    disableAutoActiveFirstOption,
    rowComponent: RowComponent,
    mode,
    withSelectAll,
    internalSearchValue,
    filterOption,
    autoComplete,
    optionFilterProp = SELECT_DEFAULT_OPTION_FILTER_PROP,
    isVirtualScrollEnabled,
    optionKeyProp = SELECT_DEFAULT_OPTION_KEY_PROP,
    value,
    virtualScrollContainer
}: SelectOptionsProps) => {
    const { t } = useTranslation();

    const optionsCount = options?.length ?? 0;
    const selectAllConfig = typeof withSelectAll === 'object' ? withSelectAll : {};
    const optionRenderer = useCallback(
        (option: InternalSelectOption) => {
            if (RowComponent) {
                const { key, ...rest } = { ...option, ...option.original };
                return <RowComponent key={key} {...rest} />;
            }
            return option.label;
        },
        [RowComponent]
    );

    const parsedOptions = useMemo(() => {
        const mergedOptions = [...options];

        if (mode === 'multiple' && withSelectAll) {
            mergedOptions.unshift({
                key: SELECT_ALL_OPTION_KEY,
                value: SELECT_ALL_OPTION_KEY,
                label: selectAllConfig.label ?? t('Select all')
            });
        }

        return parseOptions({
            options: mergedOptions,
            mapOption: (option, defaultMapFn) =>
                defaultMapFn({
                    ...option,
                    renderer: (internalOption: SelectOption) => {
                        if ('original' in internalOption) {
                            return optionRenderer(internalOption as InternalSelectOption);
                        }
                        return optionRenderer({
                            ...option,
                            original: option
                        } as InternalSelectOption);
                    }
                })
        });
    }, [options, mode, selectAllConfig?.label, t, optionRenderer, withSelectAll]);

    const defaultFilterOption = !autoComplete;
    const mergedFilterOption = filterOption ?? defaultFilterOption;
    const filteredOptions = useFilterOptions(
        parsedOptions,
        internalSearchValue,
        mergedFilterOption,
        optionFilterProp
    );

    const displayOptions = useMemo(
        () =>
            flattenOptions(filteredOptions, {
                includeGroups: true
            }),
        [filteredOptions]
    );

    const optionsElementsRef = useRef<Array<HTMLElement | null>>([]);

    const [activeIndex, setActiveIndex] = useState<number | null>(
        disableAutoActiveFirstOption ? null : 0
    );

    const selectedIndex = useMemo(() => {
        const newIndex = displayOptions.findIndex((item) => {
            return getOptionIdentifier(item, optionKeyProp) === value;
        });
        if (newIndex === -1) {
            return null;
        }
        return newIndex;
    }, [value, optionKeyProp, displayOptions]);

    const findFirstEnabledIndex = useCallback(
        () =>
            optionsElementsRef.current.findIndex(
                (el) => el && el.getAttribute('aria-disabled') !== 'true'
            ),
        []
    );

    const [initialFocusIndex] = useState(
        selectedIndex ?? activeIndex ?? (disableAutoActiveFirstOption ? -1 : 0)
    );

    const rowVirtualizer = useVirtualizer({
        count: displayOptions.length,
        getScrollElement: () => virtualScrollContainer.current,
        estimateSize: () => 50, // callback value, the real value is set in the `size` middleware
        overscan: 5,
        enabled: isVirtualScrollEnabled
    });

    const noOptions = isVirtualScrollEnabled
        ? rowVirtualizer.getTotalSize() === 0
        : displayOptions.length === 0;

    const flattenedOptions = useMemo(() => flattenOptions(options), [options]);

    const flattenedParsedOptions = useMemo(() => flattenOptions(parsedOptions), [parsedOptions]);

    return useMemo(() => {
        return {
            activeIndex,
            setActiveIndex,
            selectedIndex,
            initialFocusIndex,
            rowVirtualizer,
            noOptions,
            flattenedOptions,
            displayOptions,
            optionsElementsRef,
            optionsCount,
            optionRenderer,
            flattenedParsedOptions,
            findFirstEnabledIndex
        };
    }, [
        activeIndex,
        setActiveIndex,
        selectedIndex,
        initialFocusIndex,
        rowVirtualizer,
        noOptions,
        flattenedOptions,
        displayOptions,
        optionsCount,
        optionRenderer,
        flattenedParsedOptions,
        findFirstEnabledIndex
    ]);
};

export type SelectOptionsReturn = ReturnType<typeof useSelectOptions>;
